import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Newspaper, Calendar, Trophy, Eye, Users, Mail, MessageSquare, Clock, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    news: 0,
    events: 0,
    achievements: 0,
    publishedNews: 0,
    publishedEvents: 0,
    publishedAchievements: 0,
    totalMessages: 0,
    unreadMessages: 0,
    readMessages: 0,
    repliedMessages: 0,
  });
  const [recentMessages, setRecentMessages] = useState([]);
  const [recentNews, setRecentNews] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentData();
  }, []);

  const fetchStats = async () => {
    try {
      const [newsRes, eventsRes, achievementsRes, messagesRes] = await Promise.all([
        fetch('http://localhost/api/news.php', { credentials: 'include' }),
        fetch('http://localhost/api/events.php', { credentials: 'include' }),
        fetch('http://localhost/api/achievements.php', { credentials: 'include' }),
        fetch('http://localhost/api/contact-messages.php', { credentials: 'include' }),
      ]);

      const news = newsRes.ok ? await newsRes.json().catch(() => []) : [];
      const events = eventsRes.ok ? await eventsRes.json().catch(() => []) : [];
      const achievements = achievementsRes.ok ? await achievementsRes.json().catch(() => []) : [];
      const messages = messagesRes.ok ? await messagesRes.json().catch(() => []) : [];

      const newsArray = Array.isArray(news) ? news : [];
      const eventsArray = Array.isArray(events) ? events : [];
      const achievementsArray = Array.isArray(achievements) ? achievements : [];
      const messagesArray = Array.isArray(messages) ? messages : [];

      setStats({
        news: newsArray.length,
        events: eventsArray.length,
        achievements: achievementsArray.length,
        publishedNews: newsArray.filter((n) => n.is_published).length,
        publishedEvents: eventsArray.filter((e) => e.is_published).length,
        publishedAchievements: achievementsArray.filter((a) => a.is_published).length,
        totalMessages: messagesArray.length,
        unreadMessages: messagesArray.filter((m) => !m.is_read).length,
        readMessages: messagesArray.filter((m) => m.is_read).length,
        repliedMessages: messagesArray.filter((m) => m.is_replied).length,
      });
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  };

  const fetchRecentData = async () => {
    try {
      const [newsRes, eventsRes, messagesRes] = await Promise.all([
        fetch('http://localhost/api/news.php', { credentials: 'include' }),
        fetch('http://localhost/api/events.php', { credentials: 'include' }),
        fetch('http://localhost/api/contact-messages.php', { credentials: 'include' }),
      ]);

      const news = newsRes.ok ? await newsRes.json().catch(() => []) : [];
      const events = eventsRes.ok ? await eventsRes.json().catch(() => []) : [];
      const messages = messagesRes.ok ? await messagesRes.json().catch(() => []) : [];

      const newsArray = Array.isArray(news) ? news : [];
      const eventsArray = Array.isArray(events) ? events : [];
      const messagesArray = Array.isArray(messages) ? messages : [];

      // Son 5 mesajı al
      setRecentMessages(messagesArray.slice(0, 5));
      // Son 3 haber ve etkinlik al
      setRecentNews(newsArray.slice(0, 3));
      setRecentEvents(eventsArray.slice(0, 3));
    } catch (error) {
      console.error('Recent data fetch error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dakika önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} gün önce`;
    return formatDate(dateString);
  };

  const statCards = [
    {
      title: 'Toplam Haber',
      value: stats.news,
      published: stats.publishedNews,
      icon: <Newspaper size={32} />,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      link: '/admin/haberler',
    },
    {
      title: 'Toplam Etkinlik',
      value: stats.events,
      published: stats.publishedEvents,
      icon: <Calendar size={32} />,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      link: '/admin/etkinlikler',
    },
    {
      title: 'Toplam Başarı',
      value: stats.achievements,
      published: stats.publishedAchievements,
      icon: <Trophy size={32} />,
      color: 'from-[#C0A062] to-[#9a7d4a]',
      bgColor: 'bg-[#faf8f3]',
      textColor: 'text-[#C0A062]',
      link: '/admin/basarilar',
    },
    {
      title: 'Gelen Mesajlar',
      value: stats.totalMessages,
      published: stats.unreadMessages,
      icon: <Mail size={32} />,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      link: '/admin/mesajlar',
      isMessages: true,
      badge: stats.unreadMessages > 0 ? (
        <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
          {stats.unreadMessages} yeni
        </span>
      ) : null,
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {statCards.map((stat, index) => (
            <div
              key={index}
              onClick={() => navigate(stat.link)}
              className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 cursor-pointer hover:shadow-xl transition-all hover:scale-105"
              style={{ borderLeftColor: stat.textColor.replace('text-blue-', '#3b82f6').replace('text-purple-', '#9333ea').replace('text-green-', '#22c55e').replace('text-[#C0A062]', '#C0A062') }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 sm:p-3 rounded-lg bg-gradient-to-br ${stat.color} text-white flex-shrink-0`}>
                  {stat.icon}
                </div>
                <div className="text-right min-w-0 flex-1">
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{stat.title}</p>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Eye size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">
                    {stat.isMessages 
                      ? (stat.published > 0 ? `${stat.published} okunmamış` : 'Tümü okundu')
                      : `${stat.published} yayında`
                    }
                  </span>
                </div>
                {stat.badge}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Gelen Mesajlar */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                <Mail className="text-green-500" size={20} />
                <span>Gelen Mesajlar</span>
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/mesajlar')}
                className="flex items-center gap-2"
              >
                Tümünü Gör
                <ArrowRight size={16} />
              </Button>
            </div>
            
            {stats.unreadMessages > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm font-semibold text-red-700">
                  {stats.unreadMessages} okunmamış mesajınız var!
                </p>
              </div>
            )}

            {recentMessages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail size={48} className="mx-auto mb-2 text-gray-300" />
                <p>Henüz mesaj yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentMessages.map((message) => (
                  <div
                    key={message.id}
                    onClick={() => navigate('/admin/mesajlar')}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                      !message.is_read
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 text-sm sm:text-base break-words">{message.name}</span>
                          {(!message.is_read || message.is_read === 0) ? (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                              Yeni
                            </span>
                          ) : null}
                          {message.is_replied ? (
                            <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                              Yanıtlandı
                            </span>
                          ) : null}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{message.subject || 'Konu yok'}</p>
                        <p className="text-xs text-gray-500 line-clamp-2 break-words">{message.message}</p>
                      </div>
                      <div className="text-xs text-gray-400 sm:ml-4 whitespace-nowrap flex-shrink-0">
                        {getRelativeTime(message.created_at)}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 mt-2">
                      {message.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} />
                          {message.email}
                        </span>
                      )}
                      {message.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} />
                          {message.phone}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Son İçerikler */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="text-blue-500" size={20} />
              <span>Son İçerikler</span>
            </h2>
            
            <div className="space-y-4">
              {/* Son Haberler */}
              {recentNews.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Newspaper size={14} className="text-blue-500 flex-shrink-0" />
                      <span>Son Haberler</span>
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/admin/haberler')}
                      className="text-xs flex-shrink-0"
                    >
                      Tümü
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {recentNews.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => navigate('/admin/haberler')}
                        className="p-2 sm:p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1 break-words">
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {getRelativeTime(item.created_at)}
                            </p>
                          </div>
                          {item.is_published ? (
                            <Eye size={14} className="text-green-500 flex-shrink-0" />
                          ) : (
                            <Eye size={14} className="text-gray-300 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Son Etkinlikler */}
              {recentEvents.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Calendar size={14} className="text-purple-500 flex-shrink-0" />
                      <span>Son Etkinlikler</span>
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/admin/etkinlikler')}
                      className="text-xs flex-shrink-0"
                    >
                      Tümü
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {recentEvents.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => navigate('/admin/etkinlikler')}
                        className="p-2 sm:p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1 break-words">
                              {item.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {item.event_date ? new Date(item.event_date).toLocaleDateString('tr-TR') : 'Tarih yok'}
                            </p>
                          </div>
                          {item.is_published ? (
                            <Eye size={14} className="text-green-500 flex-shrink-0" />
                          ) : (
                            <Eye size={14} className="text-gray-300 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Hızlı İşlemler</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/admin/haberler')}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
            >
              <Newspaper className="text-blue-500 mb-2" size={24} />
              <p className="font-semibold text-gray-900">Yeni Haber</p>
              <p className="text-sm text-gray-600">Haber ekle</p>
            </button>
            <button
              onClick={() => navigate('/admin/etkinlikler')}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
            >
              <Calendar className="text-purple-500 mb-2" size={24} />
              <p className="font-semibold text-gray-900">Yeni Etkinlik</p>
              <p className="text-sm text-gray-600">Etkinlik ekle</p>
            </button>
            <button
              onClick={() => navigate('/admin/basarilar')}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-[#C0A062] hover:bg-[#faf8f3] transition-all text-left"
            >
              <Trophy className="text-[#C0A062] mb-2" size={24} />
              <p className="font-semibold text-gray-900">Yeni Başarı</p>
              <p className="text-sm text-gray-600">Başarı ekle</p>
            </button>
            <button
              onClick={() => navigate('/admin/mesajlar')}
              className="p-4 rounded-lg border-2 border-gray-200 hover:border-green-500 hover:bg-green-50 transition-all text-left relative"
            >
              {stats.unreadMessages > 0 && (
                <span className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                  {stats.unreadMessages}
                </span>
              )}
              <Mail className="text-green-500 mb-2" size={24} />
              <p className="font-semibold text-gray-900">Mesajlar</p>
              <p className="text-sm text-gray-600">Mesajları görüntüle</p>
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;

